import { teams } from "../constants/actionTypes";
import { applySlotDelta, memberCountsTowardUsedSlots } from "../helpers/teamSlots";
import { getAccountOwnerName, getTeamMemberName } from "../helpers/teamMemberDisplay";

// One toast per mutation (GROWTH-7703). `id` counts up while a toast is on screen, so a second identical toast is a new element.
const makeToast = (state, kind, params = {}) => ({ id: (state.toast?.id || 0) + 1, kind, ...params });

const defaultModals = {
  invite: false,
  updateInvite: false,
  remove: false,
  reject: false,
  viewAccess: false,
  access: false,
  leaveTeam: false
};

const defaultState = {
  preloaders: {
    page: false,
    remove: false,
    accept: false,
    invite: false,
    reject: false,
    update: false,
    access: false
  },
  error: { isShow: false, text: "" },
  modals: {
    ...defaultModals
  },
  owner: [],
  member: [],
  limits: {},
  roles: [],
  activeAccess: {},
  toast: null,
  currentTeam: { value: "", label: "", role: null },
  isLoaded: false,
  memberLoaded: false
};

export default function(state = defaultState, action) {
  const { payload, type } = action;
  switch (type) {
    case teams.SET_LOADING:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          [payload.name]: payload.isLoading
        }
      };

    case teams.GET_TEAMS:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          page: false
        },
        member: payload.member,
        owner: payload.owner,
        limits: payload.limits || {},
        roles: payload.roles || [],
        isLoaded: true
      };

    case teams.REFRESH_OWNER_TEAM:
      return {
        ...state,
        owner: payload.owner,
        limits: payload.limits || {},
        preloaders: {
          ...state.preloaders,
          remove: false,
          invite: false,
          update: false
        }
      };

    case teams.REFRESH_OWNER_TEAM_FAIL:
      return {
        ...state,
        error: {
          isShow: true,
          text: payload
        },
        preloaders: {
          ...state.preloaders,
          remove: false,
          invite: false,
          update: false
        }
      };

    case teams.REFRESH_MEMBER_TEAM:
      return {
        ...state,
        member: payload,
        preloaders: {
          ...state.preloaders,
          remove: false
        }
      };

    case teams.REFRESH_MEMBER_TEAM_FAIL:
      return {
        ...state,
        error: {
          isShow: true,
          text: payload
        },
        preloaders: {
          ...state.preloaders,
          remove: false
        }
      };

    case teams.GET_TEAMS_MEMBER:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          page: false
        },
        member: payload,
        memberLoaded: true
      };

    case teams.GET_TEAMS_MEMBER_FAIL:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          page: false
        },
        memberLoaded: true
      };

    case teams.TOGGLE_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [payload.modal]: payload.isActive
        }
      };

    case teams.CLEAR_ALL_MODALS:
      return {
        ...state,
        modals: {
          ...defaultModals
        }
      };

    case teams.ACTIVE_ACCESS:
      return {
        ...state,
        activeAccess: payload
      };

    case teams.REMOVE_ACCESS: {
      const teamMemberId = String(payload);
      const removedOwner = state.owner.find(item => String(item.team_member_id) === teamMemberId);
      const updatedOwner = state.owner.filter(item => String(item.team_member_id) !== teamMemberId);
      const updatedMember = state.member.filter(item => String(item.team_member_id) !== teamMemberId);
      const removedMember = state.member.find(item => String(item.team_member_id) === teamMemberId);
      const slotDelta = memberCountsTowardUsedSlots(removedOwner) ? -1 : 0;

      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          remove: true
        },
        modals: {
          ...state.modals,
          remove: false,
          leaveTeam: false
        },
        toast: removedOwner
          ? makeToast(state, "removed", { name: getTeamMemberName(removedOwner) })
          : makeToast(state, removedMember && String(removedMember.status) === "3" ? "cleared" : "left", {
              account: getAccountOwnerName(removedMember)
            }),
        owner: updatedOwner,
        member: updatedMember,
        limits: slotDelta ? applySlotDelta(state.limits, slotDelta) : state.limits
      };
    }

    case teams.ACCEPT_ACCESS: {
      const accepted = state.member.find(item => String(item.team_member_id) === String(action.meta?.team_member_id));

      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          accept: false
        },
        modals: {
          ...state.modals,
          accept: false
        },
        toast: makeToast(state, "accepted", { account: getAccountOwnerName(accepted) })
      };
    }

    case teams.INVITE_ACCESS:
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          invite: true
        },
        modals: {
          ...state.modals,
          invite: false
        },
        toast: makeToast(state, action.meta?.resend ? "resent" : "invited", { email: action.meta?.email }),
        limits: applySlotDelta(state.limits, 1)
      };

    case teams.REJECT_ACCESS: {
      const declined = state.member.find(item => String(item.team_member_id) === String(payload));
      // The row stays, as Declined, so the member can clear it from the list with "Remove" (GROWTH-7703).
      const updatedMember = state.member.map(item => (item === declined ? { ...item, status: 3 } : item));

      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          reject: false
        },
        modals: {
          ...state.modals,
          reject: false,
          leaveTeam: false
        },
        toast: makeToast(state, "declined", { owner: getAccountOwnerName(declined) }),
        member: updatedMember
      };
    }

    case teams.UPDATE_ACCESS: {
      const updated = state.owner.find(item => String(item.team_member_id) === String(action.meta?.team_member_id));

      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          update: false
        },
        modals: {
          ...defaultState.modals
        },
        toast: makeToast(state, "updated", { name: getTeamMemberName(updated) })
      };
    }

    case teams.CLEAR_TOAST:
      return { ...state, toast: null };

    case teams.CLEAR_ERROR:
      return {
        ...state,
        error: {
          ...state.error,
          isShow: false,
          text: ""
        }
      };

    case teams.REMOVE_ACCESS_FAIL:
    case teams.ACCEPT_ACCESS_FAIL:
    case teams.INVITE_ACCESS_FAIL:
    case teams.REJECT_ACCESS_FAIL:
    case teams.GET_TEAMS_FAIL:
    case teams.UPDATE_ACCESS_FAIL:
      return {
        ...state,
        error: {
          ...state.error,
          isShow: true,
          text: payload
        },
        preloaders: {
          ...defaultState.preloaders
        }
      };

    case teams.SET_TEAM:
      return {
        ...state,
        currentTeam: payload
      };

    default:
      return state;
  }
}
